/** @jsx isolateComponent */

import { useState } from 'react';
import type { Interpolation } from '@emotion/css';

import { FloatingBox } from '../../display/FloatingBox';
import { IconButton } from '../../navigation/IconButton';
import { Menu, MenuItem } from '../../forms/Menu';
import { Colors } from '../../../theme';
import { useClickOutside } from '../../../hooks';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';

import type { NavItemType, NavSize, OnChooseItem } from './types';

type Props = {
  /**
   * Whether to render a selected state.
   */
  shouldIndicateSelected: boolean;
  /**
   * Array of items to render in the dropdown.
   */
  items: Array<NavItemType>;
  /**
   * Callback for when an item is chosen.
   */
  onChooseItem: OnChooseItem;
  /**
   * Indicates which item is selected based on its href.
   */
  selectedItemHref: string;
  /**
   * The size to render the dropdown at.
   */
  size: NavSize;
};

const NavDropdownButtonColorTheme = {
  custom: {
    base: {
      backgroundColor: Colors.grey000,
      color: Colors.grey700,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.grey100,
      color: Colors.blue500,
    },
    focus: {
      backgroundColor: Colors.grey100,
      outlineColor: 'transparent',
      color: Colors.blue500,
    },
    selected: {
      backgroundColor: Colors.grey100,
      color: Colors.blue500,
    },
  },
};

const Styles = labelStyles('NavDropdown', {
  root: {
    position: 'relative',
  },

  summary: {
    // Hides default icon on details element
    '&::-webkit-details-marker': {
      display: 'none',
    },
    '&::before': {
      display: 'none',
    },
  },

  menu: {
    padding: 10,
    minWidth: 250,
  },
});

export const NavDropdown = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuItems = props.items.map((item) => (
    <MenuItem
      disabled={item.disabled}
      href={item.href}
      isSelected={item.href === props.selectedItemHref}
      key={item.href}
      onClick={(event: React.MouseEvent) => {
        setIsOpen(false);
        props.onChooseItem(item, event);
      }}
    >
      {item.text}
    </MenuItem>
  ));
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => {
      setIsOpen(false);
    },
  });
  // Define handlers here to avoid non-applicable linter a11y complaints.
  const detailsHandlers = {
    onClick: (event: React.MouseEvent) => event.preventDefault(),
    onKeyUp: (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    },
  };
  // Define handlers here to avoid non-applicable linter a11y complaints.
  const summaryHandlers = {
    onClick: () => setIsOpen(!isOpen),
  };
  return (
    <details
      {...detailsHandlers}
      css={Styles.root as Interpolation}
      open={isOpen}
      ref={setClickOutsideNode}
    >
      <summary
        {...summaryHandlers}
        aria-expanded={isOpen}
        aria-label="More items…"
        css={Styles.summary as Interpolation}
        role="button"
        tabIndex={0}
      >
        <IconButton
          color="custom"
          colorTheme={NavDropdownButtonColorTheme}
          icon="navMoreHoriz"
          isSelected={
            props.shouldIndicateSelected &&
            props.items.some((item) => item.href === props.selectedItemHref)
          }
          label="More items…"
          size={
            {
              medium: 'xsmall' as 'xsmall',
              large: 'small' as 'small',
            }[props.size]
          }
          tooltipPosition="west"
        />
      </summary>
      {isOpen && (
        <FloatingBox align="right">
          <div css={Styles.menu as Interpolation}>
            <Menu ariaLabel="">{menuItems}</Menu>
          </div>
        </FloatingBox>
      )}
    </details>
  );
};
